import React, { FC, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  Icon,
  IconButton,
  Paper,
  alpha,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { processedItemsService } from "../../../services/processed-items.service";
import { inventoryService } from "../../../services/inventory.service";
import { convertUnits } from "../../../utils/units";
import {
  IngredientDialog,
  ExtendedBatch,
  IngredientEntry,
} from "./components/IngredientDialog";
import { BasicInformationStep } from "./components/BasicInformationStep";
import { APIProductionDetailsStep } from "./components/ProductionDetailsStep";
import { StorageStep } from "./components/StorageStep";
import { ProcessedItemSummary } from "./components/ProcessedItemSummary";
import { IngredientsStep } from "./components/IngredientsStep";
import { Stepper as CustomStepper } from "../../../../../components/Stepper/Stepper";
import { IESDoc } from "../../../../../app.interface";
import moment from "moment";

interface ProcessedItemFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  mode: "add" | "edit";
  selectedDoc?: any;
}

export interface ProcessedItemData {
  name: string;
  api_batch_id: string;
  production_date: number;
  expiry_date: number;
  storage_location: string;
  storage_notes: string;
  category: string;
  recipe: {
    name: string;
    id: string;
    base_recipe_cost: number;
    recipe_multiplier: number;
  };
  ingredients: {
    name: string;
    total_ingredient_cost: number;
    ingredient_cost_distribution: number;
    batches: {
      id: string;
      quantity: number;
      total_ingredient_batch_cost: number;
      unit: string;
    }[];
  }[];
  total_api_cost: number;
  api_cost_distribution: number;
  total_cost: number;
  description?: string;
}

interface Recipe {
  menu_item_id: string;
  id: string;
  name: string;
  description: string;
  category: string;
  yield_quantity: number;
  yield_unit: string;
  total_cost: number;
  ingredients: RecipeIngredient[];
}

interface RecipeIngredient {
  item_id: string;
  item_type: string;
  ingredient_id: string;
  brand_name: string;
  quantity: number;
  unit: string;
  notes?: string;
  parent_unit?: string;
  selected_batch_id?: string;
}

interface ModifiedIngredient {
  item_id: string;
  item_type: string;
  ingredient_id: string;
  brand_name: string;
  modified_quantity: number;
  original_quantity: number;
  unit: string;
  notes: string;
  selected_batches: Array<{
    id: string;
    batch_id: string;
    quantity: number;
    unit: string;
    cost_per_unit: number;
    total_ingredient_batch_cost: number;
  }>;
  is_modified: boolean;
  base_unit?: string;
  required_quantity: number;
  remaining_quantity: number;
  batches: Array<{
    id: string;
    batch_id: string;
    quantity: number;
    unit: string;
    cost_per_unit: number;
    total_ingredient_batch_cost: number;
    batch_number: string;
    remaining_quantity: number;
    expiry_date: string;
  }>;
}

export const generateAPIBatchNumber = () => {
  const timestamp = new Date().getTime().toString(36);
  const random = Math.random().toString(36).substring(2, 5);
  return `API-${timestamp}-${random}`.toUpperCase();
};

const defaultFormData = {
  api_batch_id: generateAPIBatchNumber(),
  category: "",
  consumption_logs: [
    // {
    //   action: "produce",
    //   performed_by: "89d706f2-0e6c-4319-bbba-79b7c89dfd47",
    //   quantity: 20,
    //   remaining_quantity: 20,
    //   timestamp: 1743502985040,
    // },
  ],
  cost_per_unit: 0,
  created_at: 0,
  created_by: "",
  description: "",
  expiry_date: moment().add(7, "days").valueOf(),
  ingredients: [
    // {
    //   batch_id: "8575d083-9e55-4513-ab52-0cf28aff3541",
    //   cost: 0.5,
    //   ingredient_id: "db6c2fac-fc1e-49b0-95f8-70b750ab1407",
    //   is_matching_recipe: true,
    //   quantity: 5,
    //   unit: "kg",
    // },
  ],
  is_active: true,
  is_deleted: false,
  name: "",
  price: 0,
  processed_item_id: "",
  production_date: dayjs().valueOf(),
  quantity: 0,
  recipe_id: "",
  remaining_quantity: 0,
  storage_instructions: "",
  storage_place: "",
  store_id: "",
  total_cost: 0,
  unit: "",
  updated_at: dayjs().valueOf(),
  updated_by: "",
  _id: "",
};

export const ProcessedItemFormDialog: FC<ProcessedItemFormDialogProps> = ({
  open,
  onClose,
  onSave,
  mode,
  selectedDoc,
}) => {
  console.log("initialData");
  console.log(selectedDoc);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [useAutoBatchNumber, setUseAutoBatchNumber] = useState(true);

  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [modifiedIngredients, setModifiedIngredients] = useState<
    ModifiedIngredient[]
  >([]);
  const [multiplier, setMultiplier] = useState(1);
  const [ingredientDialogOpen, setIngredientDialogOpen] = useState(false);
  const [ingredientOptions, setIngredientOptions] = useState<any[]>([]);
  const [editingIngredientIndex, setEditingIngredientIndex] = useState<
    number | null
  >(null);

  const [fullProcessedItemDoc, setFullProcessedItemDoc] =
    useState<IESDoc>(defaultFormData);

  const handleNormalUpdateFullDoc = (value: any, field: string) => {
    const x = { ...fullProcessedItemDoc };
    switch (field) {
      default:
        x[field] = value;
        break;
    }

    setFullProcessedItemDoc(x);
  };

  const handleCloseIngredientDialog = () => {
    setIngredientDialogOpen(false);
    setEditingIngredientIndex(null);
  };

  const steps = [
    {
      id: "01",
      label: "Recipe Selection",
      description: "Choose a recipe and set basic details",
      icon: "restaurant_menu",
      component: (
        <BasicInformationStep
          fullDocumnet={fullProcessedItemDoc}
          selectedDocument={selectedDoc}
          selectedRecipe={selectedRecipe}
          handleUpdateFullDoc={handleNormalUpdateFullDoc}
          setSelectedRecipe={setSelectedRecipe}
        />
      ),
      optional: false,
      validation: () => !!selectedRecipe,
    },
    {
      id: "02",
      label: "Production Details",
      description: "Set batch number and production timeline",
      icon: "schedule",
      component: (
        <APIProductionDetailsStep
          fullDocumnet={fullProcessedItemDoc}
          selectedDocument={selectedDoc}
          handleUpdateFullDoc={handleNormalUpdateFullDoc}
        />
      ),
      optional: false,
      validation: () =>
        !!fullProcessedItemDoc.api_batch_id &&
        !!fullProcessedItemDoc.production_date,
    },
    {
      id: "03",
      label: "Ingredients",
      description: "Configure ingredient quantities and batches",
      icon: "inventory",
      component: (
        <IngredientsStep
          fullDocumnet={fullProcessedItemDoc}
          selectedDocument={selectedDoc}
          selectedRecipe={selectedRecipe}
          handleUpdateFullDoc={handleNormalUpdateFullDoc}
          modifiedIngredients={modifiedIngredients}
          setModifiedIngredients={setModifiedIngredients}
          setCanProceed={(canProceed) => {
            // Update the validation state for this step
            steps[2].validation = () => canProceed;
          }}
        />
      ),
      optional: false,
      validation: () => {
        // Initial validation check
        if (!modifiedIngredients.length) return false;

        // Check if all ingredients have their quantities allocated
        return !modifiedIngredients.some((ing) => {
          const totalAllocated = ing.selected_batches.reduce((total, batch) => {
            const convertedQuantity =
              batch.unit !== ing.unit
                ? convertUnits(batch.quantity, batch.unit, ing.unit) || 0
                : batch.quantity;
            return total + convertedQuantity;
          }, 0);
          return Math.abs(totalAllocated - ing.required_quantity) > 0.0001;
        });
      },
    },
    {
      id: "04",
      label: "Storage",
      description: "Set storage location and handling instructions",
      icon: "warehouse",
      component: (
        <StorageStep
          fullDocumnet={fullProcessedItemDoc}
          handleUpdateFullDoc={handleNormalUpdateFullDoc}
        />
      ),
      optional: false,
      validation: () => !!fullProcessedItemDoc.storage_place,
    },
  ];

  const handleNext = () => {
    if (steps[activeStep].validation()) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      toast.error("Please complete all required fields before proceeding");
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSave = async () => {
    try {
      if (!selectedRecipe) {
        toast.error("Please select a recipe");
        return;
      }

      if (!fullProcessedItemDoc.storage_place) {
        toast.error("Please select a storage location");
        return;
      }

      // Check if all ingredients have their quantities allocated
      const unallocatedIngredients = modifiedIngredients.filter(
        (ing) => ing.remaining_quantity > 0
      );

      if (unallocatedIngredients.length > 0) {
        toast.error(
          `Please allocate all required quantities for: ${unallocatedIngredients
            .map((ing) => ing.brand_name)
            .join(", ")}`
        );
        return;
      }

      setSaving(true);
      // Format the data for submission
      const submissionData = {
        name: fullProcessedItemDoc.name,
        description: selectedRecipe.description || "",
        category: selectedRecipe.category || "",
        recipe_id: selectedRecipe.menu_item_id,
        api_batch_id: fullProcessedItemDoc.api_batch_id,
        quantity: selectedRecipe.yield_quantity * multiplier,
        unit: selectedRecipe.yield_unit,
        production_date: fullProcessedItemDoc.production_date,
        expiry_date: fullProcessedItemDoc.expiry_date,
        storage_place: fullProcessedItemDoc.storage_place,
        storage_instructions: fullProcessedItemDoc.storage_notes,
        modified_ingredients: modifiedIngredients.map((ingredient) => ({
          item_id: ingredient.item_id,
          item_type: ingredient.item_type,
          ingredient_id: ingredient.ingredient_id,
          quantity: ingredient.modified_quantity,
          unit: ingredient.unit,
          batches: ingredient.selected_batches.map((batch) => ({
            batch_id: batch.id || batch.batch_id,
            quantity: batch.quantity,
            unit: batch.unit,
          })),
        })),
      };

      // Send the create request

      const response = await processedItemsService.createProcessedItem(
        submissionData
      );

      toast.success(
        `Processed item ${mode === "edit" ? "updated" : "created"} successfully`
      );
      await onSave(response.data);
      onClose();
    } catch (error) {
      console.error("Error saving processed item:", error);
      toast.error(
        `Failed to ${mode === "edit" ? "update" : "create"} processed item`
      );
    } finally {
      setSaving(false);
    }
  };

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < activeStep) return "complete";
    if (stepIndex === activeStep) return "current";
    return "upcoming";
  };

  const stepperSteps = steps.map((step, index) => ({
    id: step.id,
    name: step.label,
    description: step.description,
    status: getStepStatus(index) as "complete" | "current" | "upcoming",
    icon: step.icon,
  }));

  const handleStepClick = (stepId: string) => {
    const stepIndex = steps.findIndex((step) => step.id === stepId);
    if (stepIndex < activeStep) {
      setActiveStep(stepIndex);
    }
  };

  useEffect(() => {
    if (mode === "edit" && selectedDoc) {
      setFullProcessedItemDoc(selectedDoc);
      setUseAutoBatchNumber(false);
    } else {
      setFullProcessedItemDoc(defaultFormData);
      setUseAutoBatchNumber(true);
      setSelectedRecipe(null);
    }
  }, [mode, selectedDoc, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: "90vh",
          overflow: "hidden",
          bgcolor: "#F8FAFC",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          background: "#0891B2",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.1)",
            }}
          >
            <Icon>restaurant</Icon>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {mode === "edit"
                ? "Edit Processed Item"
                : "Create Processed Item"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {mode === "edit"
                ? "Modify processed item details"
                : "Create a new processed item"}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </Box>

      <Box sx={{ bgcolor: "white", borderBottom: 1, borderColor: "divider" }}>
        <CustomStepper steps={stepperSteps} onStepClick={handleStepClick} />
      </Box>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          gap: 3,
          height: "calc(100% - 200px)",
          bgcolor: "#F8FAFC",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 3,
            borderRight: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "white",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            {steps[activeStep].component}
          </Paper>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<Icon>arrow_back</Icon>}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  bgcolor: alpha("#0891B2", 0.04),
                },
              }}
            >
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              <LoadingButton
                variant="contained"
                onClick={handleSave}
                loading={saving}
                startIcon={<Icon>save</Icon>}
                sx={{
                  bgcolor: "#0891B2",
                  "&:hover": {
                    bgcolor: "#0E7490",
                  },
                }}
              >
                {mode === "edit" ? "Update" : "Create"}
              </LoadingButton>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<Icon>arrow_forward</Icon>}
                sx={{
                  bgcolor: "#0891B2",
                  "&:hover": {
                    bgcolor: "#0E7490",
                  },
                }}
              >
                Continue
              </Button>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            width: "40%",
            p: 3,
            bgcolor: "white",
            borderLeft: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
            boxShadow: "-1px 0 3px rgba(0,0,0,0.05)",
          }}
        >
          {/* <ProcessedItemSummary
            formData={formData}
            selectedRecipe={selectedRecipe}
            modifiedIngredients={modifiedIngredients}
            multiplier={multiplier}
            activeStep={activeStep}
          /> */}
        </Box>
      </DialogContent>

      {/* <IngredientDialog
        open={ingredientDialogOpen}
        onClose={handleCloseIngredientDialog}
        onSave={handleSaveIngredient}
        ingredientOptions={ingredientOptions}
        loadingIngredients={loading}
        loadBatches={handleLoadBatches}
        mode={editingIngredientIndex !== null ? "edit" : "add"}
        editData={
          editingIngredientIndex !== null
            ? {
                ingredient: ingredientOptions.find(
                  (opt) =>
                    opt.id ===
                    modifiedIngredients[editingIngredientIndex].item_id
                )!,
                quantity:
                  modifiedIngredients[editingIngredientIndex].modified_quantity,
                unit: modifiedIngredients[editingIngredientIndex].unit,
                notes: modifiedIngredients[editingIngredientIndex].notes || "",
                batches: [],
                loadingBatches: false,
                expanded: false,
              }
            : undefined
        }
      /> */}
    </Dialog>
  );
};
